import {
  classifySsoSignInError,
  isSsoSignInErrorKind,
  isSsoSignInErrorRef,
  type SsoSignInErrorKind,
  sanitizeSsoErrorMessageForLogin,
} from "@repo/shared";
import { AlertCircle, Check, Copy, MailCheck } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./sso-error-alert.i18n";

type MessageKey = Exclude<
  keyof (typeof T)["en_GB"],
  | "adminHintLabel"
  | "referenceLabel"
  | "providerMessageLabel"
  | "referenceHelp"
  | "copy"
  | "copied"
  | "linkEmailTitle"
  | "linkEmailSent"
>;
type HintKey = Extract<MessageKey, `hint${string}`>;

/** Which translated message key a kind maps to. */
const MESSAGE_BY_KIND: Record<SsoSignInErrorKind, MessageKey> = {
  cancelled: "cancelled",
  expired: "expired",
  idp_rejected_request: "idpRejectedRequest",
  idp_unavailable: "idpUnavailable",
  idp_timeout: "idpTimeout",
  token_not_verified: "tokenNotVerified",
  missing_user_info: "missingUserInfo",
  email_required: "emailRequired",
  provider_misconfigured: "providerMisconfigured",
  account_not_linked: "accountNotLinked",
  signup_disabled: "signupDisabled",
  account_unavailable: "accountUnavailable",
  last_password_admin: "lastPasswordAdmin",
  identity_changed: "identityChanged",
  link_email_sent: "unknown", // never read: link_email_sent renders its own info alert below
  unknown: "unknown",
};

/** Only these kinds carry an administrator hint (config advice, no stored value). */
const HINT_BY_KIND: Partial<Record<SsoSignInErrorKind, HintKey>> = {
  token_not_verified: "hintTokenNotVerified",
  missing_user_info: "hintMissingUserInfo",
  provider_misconfigured: "hintProviderMisconfigured",
  idp_rejected_request: "hintIdpRejectedRequest",
  account_not_linked: "hintAccountNotLinked",
};

type SsoErrorAlertProps = {
  /** The raw `?ssoError=` code; never rendered, only used to classify (fallback when `kind` is absent/invalid). */
  code: string;
  /** `?ssoKind=` — the API's closed-enum classification (QL-637). Validated against the enum before use. */
  kind?: string | null;
  /** `?ssoRef=` — a correlation reference an administrator can look up (QL-637). Rendered only when UUID-shaped. */
  reference?: string | null;
  /**
   * `?ssoMessage=` — the identity provider's `error_description` (QL-637). URL text
   * anyone can write: re-sanitised here with `sanitizeSsoErrorMessageForLogin`
   * (plain text, links/domains replaced, capped) and rendered as a text node only.
   */
  message?: string | null;
  className?: string;
};

/**
 * The SSO result shown inside the login card, above the form (QL-348). Soft
 * status surface (DESIGN.md): tint and border, the icon carries the color, the
 * text stays foreground so it reads well on the tint. `link_email_sent` is not a
 * failure (QL-350): the user has to confirm the link by e-mail first.
 *
 * QL-637 — the API classifies the (code, description) pair server-side and
 * sends the closed-enum `kind`, which selects one of this component's fixed,
 * translated texts (a spoofed `?ssoKind=` can only pick another fixed text,
 * exactly like `?ssoError=` always could), plus an optional admin hint and the
 * correlation reference. The identity provider's own message is shown below it,
 * labelled as the sign-in provider's (PO decision 2026-10-20, reversing D1). It
 * comes from the URL, so it is plain text only, with every link or domain
 * replaced and a 200-character cap.
 */
export function SsoErrorAlert({ code, kind, reference, message, className }: SsoErrorAlertProps) {
  const t = useT(T);
  const [copied, setCopied] = useState(false);

  const resolvedKind: SsoSignInErrorKind = isSsoSignInErrorKind(kind)
    ? kind
    : classifySsoSignInError(code);

  if (resolvedKind === "link_email_sent") {
    return (
      <Alert variant="info" className={className}>
        <MailCheck />
        <AlertTitle>{t.linkEmailTitle}</AlertTitle>
        <AlertDescription className="text-muted-foreground">{t.linkEmailSent}</AlertDescription>
      </Alert>
    );
  }

  const messageKey = MESSAGE_BY_KIND[resolvedKind];
  const hintKey = HINT_BY_KIND[resolvedKind];
  const showReference = isSsoSignInErrorRef(reference);
  const providerMessage = sanitizeSsoErrorMessageForLogin(message);

  const handleCopy = async () => {
    if (!reference) return;
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable: the reference is still visible and selectable.
    }
  };

  return (
    <Alert
      variant="destructive"
      className={cn("border-destructive/40 bg-destructive/10 text-foreground", className)}
    >
      <AlertCircle />
      <AlertTitle>{t.title}</AlertTitle>
      <AlertDescription className="text-muted-foreground">
        <p className="break-words">{t[messageKey]}</p>
        {providerMessage && (
          <p className="mt-2 text-xs break-words [overflow-wrap:anywhere]">
            <span className="font-medium">{t.providerMessageLabel}</span> {providerMessage}
          </p>
        )}
        {hintKey && (
          <p className="mt-2 text-xs">
            <span className="font-medium">{t.adminHintLabel}</span> {t[hintKey]}
          </p>
        )}
        {showReference && (
          <p className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span>{t.referenceLabel}</span>
            <code className="select-all font-mono">{reference}</code>
            <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
              {copied ? <Check /> : <Copy />}
              {copied ? t.copied : t.copy}
            </Button>
            <span className="text-muted-foreground">{t.referenceHelp}</span>
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}
